<template>
  <ReportingLayout :disableFacilitatorTools="true">

    <b-toast
      id="max-5"
      no-close-button
      b-toaster-top-right
      variant="danger"
      :solid="true"
    >
      <strong>
        {{ $t("max-5") }}
      </strong>
    </b-toast>

    <LocaleChanger
          class="noprint"
          :leftAlign="true"
          v-bind:emitRefresh="false"
        />

    <div style="margin: 15px">
      <div class="noprint">
        <br />
        <div class="text-center">
          <h4>{{ $t("title") }}</h4>
        </div>
        {{ $t('info')  }}
        <br/>
        <br/>
        <base-button
          size="sm"
          type="primary"
          style="white-space: normal; margin: 2px"
          v-on:click="$router.push('/admin/games')"
          >{{ $t("exit") }}
        </base-button>
      </div>
      <br />
      <vue-good-table
      class="noprint"
      :pagination-options="{
            enabled: true,
            mode: 'records',
            perPage: 50,
            nextLabel: $t('next'),
            prevLabel: $t('previous'),
            dropdownAllowAll: false,
            perPageDropdown: [50, 100, 500, 1000],
            rowsPerPageLabel: $t('rowsperpage'),
            ofLabel: 'of',
            pageLabel: 'page', // for 'pages' mode
          }"

        @on-row-click="rowClicked"
        ref="table"
        :rows="getRows"
        :columns="columns"
        styleClass="vgt-table striped condensed"
        :sort-options="{
          enabled: false,
        }"
        :row-style-class="rowStyleClassFn">

        <template slot="table-column" slot-scope="props">
              {{ $t(props.column.label) }}
          </template>

      </vue-good-table>
    </div>

    <div v-if="showClassicReport">
      <Logo/>

      <div v-for="(gameCode, index) in selectedGameCodes" :key="index">
        <CombinationReportClassic :gameCode="gameCode" :isFirst="!index"/>
      </div>
    </div>
    <div v-if="showRPSReport">      
      <CombinationReportRPS :gameCodes="selectedGameCodes" :key="reportingKey"/>
    </div>

  </ReportingLayout>
</template>

<script>
import { VueGoodTable } from "vue-good-table";
import "vue-good-table/dist/vue-good-table.css";
import { getAllGames } from "@/services/APIService";
import ReportingLayout from "@/layouts/ReportingLayout.vue";
import CombinationReportRPS from "@/components/CombinationReportRPS.vue";
import CombinationReportClassic from "@/components/CombinationReportClassic.vue";
import LocaleChanger from "@/components/LocaleChanger.vue";
import Logo from "@/components/Logo.vue";

export default {
  data() {
    return {
      columns: [
        {
          label: "name",
          field: "name",
        },
        {
          label: "opened",
          field: "opened",
          formatFn: this.formatDate,
        },
      ],
      selectedRows: [],
      rows: [],
      games: [],
      selectedGameCodes: [],
      reportingKey: 0,
    };
  },
  components: {
    VueGoodTable,
    ReportingLayout,
    CombinationReportRPS,
    LocaleChanger,
    CombinationReportClassic,
    Logo,
  },
  async mounted() {
    await this.loadItems();
  },
  computed: {
    showClassicReport() {
      if (this.selectedRows.length < 2) {
        return false;
      }

      return this.selectedRows[0].scenario;
    },
    showRPSReport() {
      if (this.selectedRows.length < 2) {
        return false;
      }

      return this.selectedRows[0].rps_scenario;
    },    
    getRows() {
      // If nothing selected, return all      
      if (this.selectedRows.length === 0) {
        return this.rows;
      }

      // Return just rows which have the same [rps] scenario as the selected one:
      const scenario = this.selectedRows[0].scenario;
      const rps_scenario = this.selectedRows[0].rps_scenario;

      return this.rows.filter((row) => {
        if (row.scenario === scenario && row.rps_scenario === rps_scenario) {
          return true;
        } else {
          return false;
        }
      });
    },
  },
  methods: {
    updateGameCodes() {
      this.selectedGameCodes = [];
      for (let i = 0; i < this.selectedRows.length; i++) {
        this.selectedGameCodes.push(this.selectedRows[i].game_code);
      }
      this.reportingKey++;
    },
    rowClicked(params) {
      // Let's check if this row was already selected
      for (let i = 0; i < this.selectedRows.length; i++) {
        if (this.selectedRows[i].id === params.row.id) {
          // Jep!
          this.selectedRows.splice(i, 1);
          this.updateGameCodes();
          return;
        }
      }
      // Nope, let's just add it

      if (this.selectedRows.length < 5) {
        this.selectedRows.push(params.row)
        this.updateGameCodes();
      } else {
        this.$bvToast.show("max-5");
      }
    },
    async loadItems() {
      await getAllGames().then((games) => {
        this.games = games;
        games.forEach((game) => {
          if (game.reporting_open) {
            this.rows.push(game);
          }
        });
      });
    },
    rowStyleClassFn(row) {
      for (let i = 0; i < this.selectedRows.length; i++) {
        if (this.selectedRows[i].id === row.id) {
          return 'bg-light';          
        }
      }
      return "";
    },
    formatDate(date) {
      return date.split("T")[0]
    }
  },
};
</script>

<style scoped>
@media print {
  @page {
    margin: 1cm;
  }
  body {
    width: 210mm;
    height: 297mm;
    margin: 25mm 25mm 25mm 25mm;
    -webkit-print-color-adjust: exact;
  }
  .noprint {
    display: none !important;
  }
  .card {
    display: none !important;
  } /* hide facilitator toolbox */
}
</style>

<i18n>
{
  "en": {
    "exit": "Exit",
    "max-5": "Maximum number of selected simulations is five.",
    "name": "Name",
    "title": "Combination reports",
    "next": "next",
    "previous": "previous",
    "rowsperpage": "rows per page",
    "info": "Combined reports are useful when you want to compare different teams, or a big team was split and participated separate simulations. Select 2-5 simulations from the table, and the combined report will be generated below the table. After selecting the first game from the list only other games using the same simulation can be selected.",
    "opened": "Simulation opened"
  },
  "sv": {
    "exit": "Avsluta",
    "max-5": "Maximalt antal valda simuleringar är fem.",
    "name": "Simuleringens namn",
    "title": "Kombinerade rapporter",
    "next": "nästa",
    "previous": "föregående",
    "rowsperpage": "rader på sidan",
    "info": "Kombinerade rapporter är användbara när du vill jämföra olika lag eller när ett stort lag har delats upp och deltagit i separata simuleringar. Välj 2-5 simuleringar i tabellen och den kombinerade rapporten genereras under tabellen. Efter att ha valt den första matchen från listan kan endast andra matcher med samma simulering väljas.",
    "opened": "Simuleringen öppnad"
  },
  "fi": {
    "exit": "Poistu",
    "max-5": "Valittujen simulaatioiden enimmäismäärä on viisi.",
    "name": "Simulaation nimi",
    "title": "Yhdistelmäraportit",
    "next": "seuraava",
    "previous": "edellinen",
    "rowsperpage": "rivejä sivulla",
    "info": "Yhdistelmäraporttien avulla voit esimerkiksi vertailla erilaisia tiimejä. Voit myös yhdistää tulokset, jos iso tiimi on jaettu pelaamaan useita simulaatioita. Valitse taulukosta 2-5 simulaatiota. Yhdistelmäraportti luodaan taulukon alle. Valittuasi ensimmäisen simulaation näytetään taulukossa vain muut samaa simulaatiota hyödyntäneet pelikerrat.",
    "opened": "Simulaatio avattu"
  },
  "de": {
    "exit": "Ausgang",
    "max-5": "Die maximale Anzahl der ausgewählten Simulationen beträgt fünf.",
    "name": "Name",
    "title": "Kombinierte Berichte",
    "next": "weiter",
    "previous": "zurück",
    "rowsperpage": "Zeilen pro Seite",
    "info": "Kombinerade rapporter är användbara när du vill jämföra olika lag eller när ett stort lag har delats upp och deltagit i separata simuleringar. Välj 2-5 simuleringar i tabellen och den kombinerade rapporten genereras unter tabellen. Efter att ha valt den ersten matchen från listan kan endast andere matcher mit samma simulering väljas.",
    "opened": "Öffnungszeit"
  },
  "ko": {
    "exit": "나가기",
    "max-5": "선택한 시뮬레이션의 최대 수는 5개입니다.",
    "name": "이름",
    "title": "통합 결과보고서",
    "next": "다음",
    "previous": "이전",
    "rowsperpage": "한 페이지당 표시 개수",
    "info": "통합 결과보고서는 여러 팀의 결과를 비교하는 경우 또는 한 조직이 여러 팀으로 나뉘어 시뮬레이션에 참여한 경우에 사용할 수 있습니다. 아래 목록에서 시뮬레이션을 선택하면 화면 하단에서 보고서를 확인할 수 있습니다. 동일한 모듈의 시뮬레이션 중 최대 5개까지 선택할 수 있습니다.",
    "opened": "개설 날짜"
  }
}
</i18n>
